import * as z from "zod";

function isValidRegex(pattern: string): boolean {
  try {
    new RegExp(pattern, "i");
    return true;
  } catch {
    return false;
  }
}

const MAX_REGEX_LENGTH = 200;

const QUANTIFIED_GROUP_WITH_QUANTIFIER =
  /\((?:[^()\\]|\\.)*(?:[+*]|\{\d+,\d*\})(?:[^()\\]|\\.)*\)(?:[+*]|\{\d+,\d*\})/;

const BACKREFERENCE = /\\[1-9]|\\k</;

export function isSafeRegex(pattern: string): boolean {
  return (
    !QUANTIFIED_GROUP_WITH_QUANTIFIER.test(pattern) &&
    !BACKREFERENCE.test(pattern)
  );
}

const RegexPattern = z
  .string()
  .min(1)
  .max(MAX_REGEX_LENGTH)
  .refine(isValidRegex, { message: "Invalid regular expression" })
  .refine(isSafeRegex, {
    message:
      "Regular expression can't repeat a group that itself repeats or use backreferences",
  });

const BomTemplateFacetAlias_ = z.object({
  pattern: RegexPattern,
  value: z.string().min(1),
});
export const BomTemplateFacetAlias: z.ZodType<BomTemplateFacetAlias> =
  BomTemplateFacetAlias_;
export interface BomTemplateFacetAlias extends z.infer<
  typeof BomTemplateFacetAlias_
> {}

const BomTemplateFacetSource_ = z.discriminatedUnion("from", [
  z.object({
    from: z.literal("option"),
    optionNames: z.array(z.string().min(1)).min(1),
    aliases: z.array(BomTemplateFacetAlias_).optional(),
  }),
  z.object({
    from: z.literal("familyTitle"),
    aliases: z.array(BomTemplateFacetAlias_).min(1),
  }),
]);
export const BomTemplateFacetSource: z.ZodType<BomTemplateFacetSource> =
  BomTemplateFacetSource_;
export type BomTemplateFacetSource = z.infer<typeof BomTemplateFacetSource_>;

const BomTemplateFacet_ = z.object({
  sources: z.array(BomTemplateFacetSource_).min(1),
});
export const BomTemplateFacet: z.ZodType<BomTemplateFacet> = BomTemplateFacet_;
export interface BomTemplateFacet extends z.infer<typeof BomTemplateFacet_> {}

const BomTemplateSlotQuantity_ = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("fixed"),
    inputQuantity: z.number().int().positive(),
  }),
  z.object({
    kind: z.literal("yieldTable"),
    outputFacet: z.string().min(1),
    sourceFacet: z.string().min(1),
  }),
]);
export const BomTemplateSlotQuantity: z.ZodType<BomTemplateSlotQuantity> =
  BomTemplateSlotQuantity_;
export type BomTemplateSlotQuantity = z.infer<typeof BomTemplateSlotQuantity_>;

const BomTemplateTieBreak_ = z.object({
  facet: z.string().min(1),
  kind: z.literal("areaRatio"),
});
export const BomTemplateTieBreak: z.ZodType<BomTemplateTieBreak> =
  BomTemplateTieBreak_;
export interface BomTemplateTieBreak extends z.infer<
  typeof BomTemplateTieBreak_
> {}

const BomTemplatePreferredSource_ = z.object({
  output: RegexPattern,
  sources: z.array(RegexPattern).min(1),
});
export const BomTemplatePreferredSource: z.ZodType<BomTemplatePreferredSource> =
  BomTemplatePreferredSource_;
export interface BomTemplatePreferredSource extends z.infer<
  typeof BomTemplatePreferredSource_
> {}

const BomTemplateNearestLarger_ = z.object({
  facet: z.string().min(1),
  order: z.array(RegexPattern).min(2),
  tieBreak: BomTemplateTieBreak_.optional(),
  preferredSources: z.array(BomTemplatePreferredSource_).optional(),
});
export const BomTemplateNearestLarger: z.ZodType<BomTemplateNearestLarger> =
  BomTemplateNearestLarger_;
export interface BomTemplateNearestLarger extends z.infer<
  typeof BomTemplateNearestLarger_
> {}

const BomTemplateSlot_ = z.object({
  name: z.string().min(1),
  sourceSides: z.array(z.string().min(1)).min(1),
  equalFacets: z.array(z.string().min(1)),
  nearestLarger: BomTemplateNearestLarger_.optional(),
  quantity: BomTemplateSlotQuantity_,
});
export const BomTemplateSlot: z.ZodType<BomTemplateSlot> = BomTemplateSlot_;
export interface BomTemplateSlot extends z.infer<typeof BomTemplateSlot_> {}

const BomTemplateYield_ = z.object({
  output: z.string().min(1),
  source: z.string().min(1),
  inputQuantity: z.number().int().positive(),
  outputQuantity: z.number().int().positive(),
});
export const BomTemplateYield: z.ZodType<BomTemplateYield> = BomTemplateYield_;
export interface BomTemplateYield extends z.infer<typeof BomTemplateYield_> {}

function hasDuplicateYields(rows: BomTemplateYield[]): boolean {
  const keys = rows.map((row) => `${row.output}\u0000${row.source}`);
  return new Set(keys).size !== keys.length;
}

const BomTemplateSpec_ = z
  .object({
    outputSides: z.array(z.string().min(1)).min(1),
    outputQuantity: z.number().int().positive().default(1),
    slots: z.array(BomTemplateSlot_).min(1),
    yields: z.array(BomTemplateYield_).default([]),
    bomTags: z
      .array(z.string().trim().min(1))
      .default([])
      .transform((tags) => [...new Set(tags)]),
  })
  .superRefine((spec, ctx) => {
    const names = spec.slots.map((slot) => slot.name);
    if (new Set(names).size !== names.length) {
      ctx.addIssue({
        code: "custom",
        message: "Slot names must be unique",
        path: ["slots"],
      });
    }
    const yieldSlots = spec.slots.filter(
      (slot) => slot.quantity.kind === "yieldTable",
    );
    if (yieldSlots.length > 1) {
      ctx.addIssue({
        code: "custom",
        message: "At most one slot may take its quantity from the yield table",
        path: ["slots"],
      });
    }
    if (hasDuplicateYields(spec.yields)) {
      ctx.addIssue({
        code: "custom",
        message: "Yield rows must be unique by output and source",
        path: ["yields"],
      });
    }
  });
export const BomTemplateSpec: z.ZodType<
  BomTemplateSpec,
  z.input<typeof BomTemplateSpec_>
> = BomTemplateSpec_;
export interface BomTemplateSpec extends z.infer<typeof BomTemplateSpec_> {}

const BomTemplateBindingSide_ = z.object({
  facets: z.record(z.string().min(1), BomTemplateFacet_),
});
export const BomTemplateBindingSide: z.ZodType<BomTemplateBindingSide> =
  BomTemplateBindingSide_;
export interface BomTemplateBindingSide extends z.infer<
  typeof BomTemplateBindingSide_
> {}

const BomTemplateBindingSpec_ = z
  .object({
    sides: z.record(z.string().min(1), BomTemplateBindingSide_),
    yieldOverrides: z.array(BomTemplateYield_).default([]),
  })
  .superRefine((spec, ctx) => {
    if (hasDuplicateYields(spec.yieldOverrides)) {
      ctx.addIssue({
        code: "custom",
        message: "Yield overrides must be unique by output and source",
        path: ["yieldOverrides"],
      });
    }
  });
export const BomTemplateBindingSpec: z.ZodType<
  BomTemplateBindingSpec,
  z.input<typeof BomTemplateBindingSpec_>
> = BomTemplateBindingSpec_;
export interface BomTemplateBindingSpec extends z.infer<
  typeof BomTemplateBindingSpec_
> {}

export function bindingCoversTemplate(
  template: BomTemplateSpec,
  binding: BomTemplateBindingSpec,
): string[] {
  const problems: string[] = [];
  const sideNames = new Set(Object.keys(binding.sides));
  const requireSide = (side: string) => {
    if (!sideNames.has(side)) {
      problems.push(`Binding has no side named ${side}`);
    }
  };
  for (const side of template.outputSides) {
    requireSide(side);
  }
  const requireFacet = (side: string, facet: string, slotName: string) => {
    const sideSpec = binding.sides[side];
    if (sideSpec && !(facet in sideSpec.facets)) {
      problems.push(
        `Side ${side} has no facet ${facet} needed by slot ${slotName}`,
      );
    }
  };
  for (const slot of template.slots) {
    for (const side of slot.sourceSides) {
      requireSide(side);
    }
    const shared = [
      ...slot.equalFacets,
      ...(slot.nearestLarger ? [slot.nearestLarger.facet] : []),
      ...(slot.nearestLarger?.tieBreak
        ? [slot.nearestLarger.tieBreak.facet]
        : []),
    ];
    for (const side of [...template.outputSides, ...slot.sourceSides]) {
      for (const facet of shared) {
        requireFacet(side, facet, slot.name);
      }
    }
    if (slot.quantity.kind === "yieldTable") {
      for (const side of template.outputSides) {
        requireFacet(side, slot.quantity.outputFacet, slot.name);
      }
      for (const side of slot.sourceSides) {
        requireFacet(side, slot.quantity.sourceFacet, slot.name);
      }
    }
  }
  return [...new Set(problems)];
}
